/**
 * Migration Script V2: Legacy Areas to New Hierarchy Schema
 * 
 * Improved version that properly handles existing StoreRecords
 * - Checks if stores exist before creating
 * - Updates existing stores with hierarchy data
 * - Better error handling and reporting
 */

import { API, graphqlOperation } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import { getStoreLocation } from './storeLocationMapping';
import { getToastSystemName } from './toastNameMapping';

// Import your GraphQL operations
import { 
  listAreas2, 
  storesByClientSorted, 
  getStoreRecord, 
  listHierarchyDefinitions,
  listOrgUnits,
  getOrgUnit 
} from 'graphql/queries';
import { 
  createOrgUnit,
  updateOrgUnit, 
  updateStoreRecord,
  createStoreRecord,
  // createManagerAssignmentHistory, // REMOVED: ManagerAssignmentHistory removed from schema
  createHierarchyDefinition 
} from 'graphql/mutations';

// Helper function to remove __typename fields from objects
function removeTypename(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeTypename(item));
  }
  
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key !== '__typename') {
      cleaned[key] = removeTypename(value);
    }
  }
  return cleaned;
}

class HierarchyMigrationV2 {
  constructor(clientId = 'grm') {
    this.clientId = clientId;
    this.migrationId = uuidv4();
    this.results = {
      areas: { created: 0, updated: 0, errors: [] },
      stores: { created: 0, updated: 0, skipped: 0, errors: [] },
      managers: { created: 0, errors: [] },
      hierarchyDefinition: null
    };
    this.existingStores = new Map();
    this.existingOrgUnits = new Map();
  }

  /**
   * Main migration orchestrator
   */
  async migrate() {
    console.log(`🚀 Starting hierarchy migration V2 for client: ${this.clientId}`);
    console.log(`📋 Migration ID: ${this.migrationId}`);
    
    try {
      // Step 0: Fetch existing records
      await this.fetchExistingStores();
      console.log(`📊 Found ${this.existingStores.size} existing store records`);
      
      await this.fetchExistingOrgUnits();
      console.log(`📊 Found ${this.existingOrgUnits.size} existing OrgUnits`);

      // Step 1: Fetch legacy data
      const legacyData = await this.fetchLegacyData();
      console.log(`📊 Found ${legacyData.length} areas with stores`);

      // Step 2: Create hierarchy definition
      await this.createHierarchyDefinition();

      // Step 3: Migrate areas to OrgUnits
      await this.migrateAreas(legacyData);

      // Step 4: Migrate stores with operational data
      await this.migrateStores(legacyData);

      // Step 5: Create manager assignments
      await this.migrateManagerAssignments(legacyData);

      // Step 6: Validation
      await this.validateMigration();

      console.log('✅ Migration completed successfully!');
      return this.results;

    } catch (error) {
      console.error('❌ Migration failed:', error);
      throw error;
    }
  }

  /**
   * Fetch existing store records
   */
  async fetchExistingStores() {
    try {
      const result = await API.graphql(
        graphqlOperation(storesByClientSorted, { clientId: this.clientId })
      );
      const stores = result.data.storesByClientSorted?.items || [];
      
      // Create multiple maps for different matching strategies
      this.existingStores = new Map(); // By ID
      this.storesByName = new Map(); // By storeName
      this.storesByNumber = new Map(); // By storeNumber
      this.storesByToastId = new Map(); // By toastId
      
      stores.forEach(store => {
        // Map by ID
        this.existingStores.set(store.id, store);
        
        // Map by storeName
        if (store.storeName) {
          this.storesByName.set(store.storeName, store);
        }
        
        // Map by storeNumber
        if (store.storeNumber) {
          this.storesByNumber.set(store.storeNumber, store);
        }
        
        // Map by toastId (if available in metadata)
        if (store.metadata) {
          try {
            const metadata = JSON.parse(store.metadata);
            if (metadata.toastId) {
              this.storesByToastId.set(metadata.toastId, store);
            }
          } catch (e) {
            // Ignore JSON parse errors
          }
        }
      });
      
      console.log(`📊 Loaded ${stores.length} StoreRecords with multiple lookup maps`);
      
    } catch (error) {
      console.error('Failed to fetch existing stores:', error);
      throw new Error(`Existing stores fetch failed: ${error.message}`);
    }
  }

  /**
   * Fetch existing OrgUnits
   */
  async fetchExistingOrgUnits() {
    try {
      const result = await API.graphql(
        graphqlOperation(listOrgUnits, {
          filter: {
            clientId: { eq: this.clientId }
          },
          limit: 1000 // Ensure we get all OrgUnits
        })
      );
      const orgUnits = result.data.listOrgUnits?.items || [];
      
      // Create map for quick lookup
      orgUnits.forEach(orgUnit => {
        this.existingOrgUnits.set(orgUnit.id, orgUnit);
      });
      
    } catch (error) {
      console.error('Failed to fetch existing OrgUnits:', error);
      throw new Error(`Existing OrgUnits fetch failed: ${error.message}`);
    }
  }

  /**
   * Fetch legacy area data
   */
  async fetchLegacyData() {
    try {
      // The listAreas2 query requires a report parameter
      const result = await API.graphql(graphqlOperation(listAreas2, {
        report: 'migration',  // Use a specific report identifier for migration
        user: null,
        role: null
      }));
      const allAreas = result.data.listAreas2 || [];
      
      // Include all areas (including Ghost Kitchen for completeness)
      console.log(`📊 Total areas found: ${allAreas.length}`);
      allAreas.forEach(area => {
        console.log(`  - ${area.areaName}: ${area.stores.length} stores`);
      });
      
      return allAreas;
    } catch (error) {
      console.error('Failed to fetch legacy data:', error);
      throw new Error(`Legacy data fetch failed: ${error.message}`);
    }
  }

  /**
   * Create or verify hierarchy definition for this client
   */
  async createHierarchyDefinition() {
    try {
      // First check if a hierarchy definition already exists for this client
      const existingResult = await API.graphql(
        graphqlOperation(listHierarchyDefinitions, {
          filter: {
            clientId: { eq: this.clientId },
            version: { eq: 1 }
          }
        })
      );
      
      const existingDefinitions = existingResult.data.listHierarchyDefinitions?.items || [];
      
      if (existingDefinitions.length > 0) {
        console.log('ℹ️  Hierarchy definition already exists for client, continuing...');
        this.results.hierarchyDefinition = existingDefinitions[0];
        return;
      }
      
      // If no existing definition, create a new one
      const hierarchyDefinition = {
        clientId: this.clientId,
        version: 1,
        effectiveDate: new Date().toISOString(),
        levels: [
          {
            levelId: "ORGLEVEL_1",
            label: "Area",
            order: 1,
            isUsed: true,
            introducedInVersion: 1
          }
        ],
        changeReason: "Initial migration from legacy system",
        createdBy: "migration-script-v2"
      };

      const result = await API.graphql(
        graphqlOperation(createHierarchyDefinition, { input: hierarchyDefinition })
      );
      this.results.hierarchyDefinition = result.data.createHierarchyDefinition;
      console.log('✅ Hierarchy definition created');
      
    } catch (error) {
      console.error('Failed to create or verify hierarchy definition:', error);
      throw error;
    }
  }

  /**
   * Migrate areas to OrgUnit table (ORGLEVEL_1)
   */
  async migrateAreas(areas) {
    console.log(`📋 Processing ${areas.length} areas...`);

    for (const area of areas) {
      try {
        // Check if OrgUnit already exists
        const existingOrgUnit = this.existingOrgUnits.get(area.areaId);
        
        if (existingOrgUnit) {
          console.log(`ℹ️  OrgUnit already exists: ${area.areaName} (${area.areaId}) - skipping`);
          this.results.areas.updated++;
        } else {
          // Create new OrgUnit
          const orgUnit = {
            id: area.areaId,
            clientId: this.clientId,
            orgLevel: "ORGLEVEL_1",
            name: area.areaName,
            displayName: area.areaName,
            description: `${area.areaType} - ${area.stores.length} stores`,
            
            // Manager info
            primaryManagerId: this.findPrimaryManager(area.stores),
            primaryManagerName: this.findPrimaryManagerName(area.stores),
            
            // Cached store count
            storeCount: area.stores.length,
            
            // Operational metadata
            metadata: JSON.stringify({
              areaType: area.areaType,
              migrationDate: new Date().toISOString(),
              migrationId: this.migrationId,
              legacyAreaId: area.areaId,
              isGhostKitchen: area.areaName === 'Ghost Kitchen'
            }),
            
            // Mark Ghost Kitchen as inactive
            isActive: area.areaName !== 'Ghost Kitchen',
            createdBy: "migration-script-v2",
            lastModified: new Date().toISOString()
          };

          const result = await API.graphql(
            graphqlOperation(createOrgUnit, { input: orgUnit })
          );
          
          this.results.areas.created++;
          console.log(`✅ Created OrgUnit: ${area.areaName} (${area.areaId})`);
        }

      } catch (error) {
        console.error(`❌ Failed to process OrgUnit for area ${area.areaName}:`, error);
        this.results.areas.errors.push({
          areaId: area.areaId,
          areaName: area.areaName,
          error: error.message
        });
      }
    }
  }

  /**
   * Migrate stores with operational data in metadata
   */
  async migrateStores(areas) {
    console.log(`📋 Processing stores...`);

    for (const area of areas) {
      for (const store of area.stores) {
        try {
          // Find matching StoreRecord using multiple strategies (same as StoreMigrationManager)
          let existingStore = null;
          let matchType = null;
          
          // FIRST: Try by shortName matching storeName
          if (this.storesByName.has(store.shortName)) {
            existingStore = this.storesByName.get(store.shortName);
            matchType = 'shortName->storeName';
          }
          // SECOND: Try by longName containing storeName
          else if (store.longName && !existingStore) {
            for (const [storeName, record] of this.storesByName) {
              if (store.longName.includes(storeName)) {
                existingStore = record;
                matchType = 'longName contains storeName';
                break;
              }
            }
          }
          // THIRD: Try by toastId
          else if (store.toastId && this.storesByToastId.has(store.toastId)) {
            existingStore = this.storesByToastId.get(store.toastId);
            matchType = 'toastId';
          }
          // FOURTH: Try by storeNumber matching shortName
          else if (this.storesByNumber.has(store.shortName)) {
            existingStore = this.storesByNumber.get(store.shortName);
            matchType = 'shortName->storeNumber';
          }
          // LAST: Try by ID (unlikely to match)
          else if (this.existingStores.has(store.storeId)) {
            existingStore = this.existingStores.get(store.storeId);
            matchType = 'id';
          }
          
          // Skip stores that don't have a corresponding StoreRecord
          if (!existingStore) {
            console.log(`⏭️  Skipping store without StoreRecord: ${store.shortName} (ID: ${store.storeId})`);
            this.results.stores.skipped++;
            continue;
          }
          
          // Update existing store with hierarchy data
          console.log(`🔄 Updating existing store: ${store.shortName} (matched by ${matchType})`);
          
          // Preserve existing metadata
          let existingMetadata = {};
          if (existingStore.metadata) {
            try {
              existingMetadata = JSON.parse(existingStore.metadata);
            } catch (e) {
              console.warn('Could not parse existing metadata:', e);
            }
          }
          
          // Get location data for address
          const locationData = getStoreLocation(store.shortName);
          
          // Build address object if location data exists
          let address = existingStore.address; // Keep existing address
          if (locationData && locationData.address) {
            address = {
              street_1: locationData.address.street1,
              street_2: locationData.address.street2 || null,
              city: locationData.address.city,
              state: locationData.address.state,
              zipCode: locationData.address.zip,
              country: locationData.address.country
            };
          }
          
          // Build external systems array
          let externalSystems = [];
          
          // Always add Aloha system
          externalSystems.push({
            systemType: 'ALOHA_POS',
            systemId: store.longName || store.shortName,
            systemName: store.longName || store.shortName,
            isActive: true,
            isPrimaryPOS: !store.toastId, // Aloha is primary if no Toast
            lastSyncAt: null,
            syncStatus: 'ACTIVE'
          });
          
          // Add Toast system if toastId exists
          if (store.toastId) {
            externalSystems.push({
              systemType: 'TOAST_POS',
              systemId: store.toastId,
              systemName: getToastSystemName(store.shortName), // Use mapped Toast name
              isActive: true,
              isPrimaryPOS: true, // Toast is primary when present
              lastSyncAt: null,
              syncStatus: 'ACTIVE'
            });
          }
          
          // Extract manager IDs from store's gm array
          const managerUserIds = store.gm || existingStore.managerUserIds || [];
          
          // Determine ownership type based on store type
          const ownershipType = store.type === 'B&M' ? 'Company' : (existingStore.ownershipType || 'Company');
          
          // Clean metadata - only include the fields from listAreas2 data
          const cleanedMetadata = {
            ...existingMetadata, // Preserve ALL existing metadata including goLiveDate
            // Add theoretical hours without __typename
            fohBaseTheoHrs: removeTypename(store.fohBaseTheoHrs),
            bohBaseTheoHrs: removeTypename(store.bohBaseTheoHrs),
            // Add thresholds without __typename
            thresholds: removeTypename(store.thresholds),
            // Store characteristics that should be in metadata
            legacyStatus: store.status,
            legacyType: store.type,
            legacyStoreId: store.storeId
          };
          
          const updateInput = {
            id: existingStore.id,  // Use the StoreRecord's ID, not the legacy storeId
            
            // Hierarchy assignment
            orgLevel_1: area.areaId,
            
            // Address from location mapping
            address: address,
            
            // External systems (Toast)
            externalSystems: externalSystems,
            
            // Manager assignments
            managerUserIds: managerUserIds,
            
            // Ownership type
            ownershipType: ownershipType,
            
            // Toast ID at top level
            toastId: store.toastId || existingStore.toastId,
            
            // Cleaned metadata
            metadata: JSON.stringify(cleanedMetadata),
            
            // Update hierarchy change tracking
            lastHierarchyChange: new Date().toISOString(),
            lastHierarchyChangeBy: "migration-script-v2"
          };
          
          // Debug: Log the update input
          console.log(`📝 Update input for ${store.shortName}:`, JSON.stringify(updateInput, null, 2));
          
          const result = await API.graphql(
            graphqlOperation(updateStoreRecord, { input: updateInput })
          );
          
          this.results.stores.updated++;
          console.log(`✅ Updated StoreRecord: ${store.shortName} (${store.storeId})`);

        } catch (error) {
          console.error(`❌ Failed to update StoreRecord for ${store.shortName}:`, error);
          this.results.stores.errors.push({
            storeId: store.storeId,
            storeName: store.shortName,
            areaName: area.areaName,
            error: error.message,
            action: 'update'
          });
        }
      }
    }
    
    console.log(`📊 Store processing complete: ${this.results.stores.updated} updated, ${this.results.stores.skipped} skipped`);
  }

  /**
   * Create ManagerAssignment records from legacy GM data
   * 
   * Creates both store-level (from store.gm) and area-level (from area.areaManager) assignments
   */
  async migrateManagerAssignments(areas) {
    console.log(`📋 Creating manager assignments (store and area level)...`);
    console.log(`ℹ️  Using unified ManagerAssignment approach for all management levels`);

    // Track all manager assignments
    const areaManagers = new Set(); // Track unique area managers
    let totalStoreManagers = 0;
    let storeAssignmentsCreated = 0;
    let areaAssignmentsCreated = 0;
    
    // First, collect area managers from listAreas2.areaManager field
    const areaManagerMap = new Map(); // areaManagerId -> Set of areaIds
    
    // Collect all store-manager relationships
    const storeManagerMap = new Map(); // managerId -> { storeData[] }
    const storeToExistingId = new Map(); // legacy storeId -> StoreRecord.id mapping
    
    for (const area of areas) {
      // Skip Ghost Kitchen
      if (area.areaName === 'Ghost Kitchen') continue;
      
      // Collect area manager from areaManager field
      if (area.areaManager) {
        if (!areaManagerMap.has(area.areaManager)) {
          areaManagerMap.set(area.areaManager, new Set());
        }
        areaManagerMap.get(area.areaManager).add(area.areaId);
        areaManagers.add(area.areaManager);
      }
      
      // Collect store managers from store.gm arrays
      for (const store of area.stores) {
        // Find the corresponding StoreRecord for this legacy store
        let existingStoreId = null;
        
        // Use same matching logic as in migrateStores
        if (this.storesByName.has(store.shortName)) {
          existingStoreId = this.storesByName.get(store.shortName).id;
        } else if (store.longName) {
          for (const [storeName, record] of this.storesByName) {
            if (store.longName.includes(storeName)) {
              existingStoreId = record.id;
              break;
            }
          }
        } else if (store.toastId && this.storesByToastId.has(store.toastId)) {
          existingStoreId = this.storesByToastId.get(store.toastId).id;
        } else if (this.storesByNumber.has(store.shortName)) {
          existingStoreId = this.storesByNumber.get(store.shortName).id;
        }
        
        if (!existingStoreId) {
          console.log(`⏭️  Skipping managers for unmapped store: ${store.shortName}`);
          continue;
        }
        
        // Map legacy storeId to actual StoreRecord.id
        storeToExistingId.set(store.storeId, existingStoreId);
        
        if (store.gm && Array.isArray(store.gm)) {
          totalStoreManagers += store.gm.length;
          
          for (const managerId of store.gm) {
            if (!storeManagerMap.has(managerId)) {
              storeManagerMap.set(managerId, { stores: [] });
            }
            
            storeManagerMap.get(managerId).stores.push({
              legacyStoreId: store.storeId,
              storeRecordId: existingStoreId,
              storeName: store.shortName,
              areaId: area.areaId
            });
          }
        }
      }
    }
    
    console.log(`📊 Manager Analysis:`);
    console.log(`   - Area managers (from areaManager field): ${areaManagers.size}`);
    console.log(`   - Store managers (from gm arrays): ${storeManagerMap.size}`);
    console.log(`   - Total store-manager relationships: ${totalStoreManagers}`);

    // Create ManagerAssignment records
    console.log(`\n📝 Creating ManagerAssignment records...`);
    
    // First, create area-level assignments from areaManager field
    console.log(`\n📝 Creating area-level assignments...`);
    for (const [areaManagerId, areaIds] of areaManagerMap) {
      for (const areaId of areaIds) {
        try {
          const effectiveDate = new Date().toISOString();
          const lastModified = new Date().toISOString();
          
          const areaAssignment = {
            clientId: this.clientId,
            userId: areaManagerId,
            managementLevel: "ORGLEVEL_1",
            orgUnitId: areaId,  // Single ID, not array
            isActive: true,
            effectiveDate: effectiveDate,
            assignedBy: "migration-script-v2",
            assignmentReason: `Area manager from listAreas2.areaManager - migrated from legacy data`,
            lastModified: lastModified
          };
          
          console.log(`   Creating area-level assignment: ${areaManagerId} -> Area ${areaId}`);
          
          // REMOVED: createManagerAssignmentHistory no longer exists
          // const result = await API.graphql(
          //   graphqlOperation(createManagerAssignmentHistory, { input: areaAssignment })
          // );
          
          areaAssignmentsCreated++;
          
        } catch (error) {
          if (error.errors?.[0]?.message?.includes('already exists')) {
            console.log(`   ℹ️  Area assignment already exists: ${areaManagerId} -> ${areaId}`);
          } else {
            console.error(`   ❌ Failed to create area assignment: ${areaManagerId} -> ${areaId}:`, error.message);
            this.results.managers.errors.push({
              managerId: areaManagerId,
              areaId,
              level: 'ORGLEVEL_1',
              error: error.message
            });
          }
        }
      }
    }
    
    // Then, create store-level assignments from store.gm arrays
    console.log(`\n📝 Creating store-level assignments...`);
    for (const [managerId, managerData] of storeManagerMap) {
      for (const storeInfo of managerData.stores) {
        try {
          const effectiveDate = new Date().toISOString();
          const lastModified = new Date().toISOString();
          
          // Validate required fields
          if (!managerId || !this.clientId || !storeInfo.storeRecordId) {
            console.error(`   ⚠️  Skipping invalid assignment: manager=${managerId}, store=${storeInfo.storeName}`);
            continue;
          }
          
          const storeAssignment = {
            clientId: this.clientId,
            userId: managerId,
            managementLevel: "LOCATION_LEVEL",
            orgUnitId: storeInfo.storeRecordId, // StoreRecord.id (single ID)
            isActive: true,
            effectiveDate: effectiveDate,
            assignedBy: "migration-script-v2",
            assignmentReason: `Store manager from store.gm array - migrated from legacy data`,
            lastModified: lastModified
          };
          
          console.log(`   Creating store-level assignment: ${managerId} -> ${storeInfo.storeName} (${storeInfo.storeRecordId})`);
          
          // REMOVED: createManagerAssignmentHistory no longer exists
          // const result = await API.graphql(
          //   graphqlOperation(createManagerAssignmentHistory, { input: storeAssignment })
          // );
          
          storeAssignmentsCreated++;
          
        } catch (error) {
          if (error.errors?.[0]?.message?.includes('already exists')) {
            console.log(`   ℹ️  Store assignment already exists: ${managerId} -> ${storeInfo.storeName}`);
          } else {
            console.error(`   ❌ Failed to create store assignment: ${managerId} -> ${storeInfo.storeName}:`, error.message);
            this.results.managers.errors.push({
              managerId,
              storeId: storeInfo.storeRecordId,
              storeName: storeInfo.storeName,
              level: 'LOCATION_LEVEL',
              error: error.message
            });
          }
        }
      }
    }
    
    // Update results
    this.results.managers.created = storeAssignmentsCreated + areaAssignmentsCreated;
    
    // Log final summary
    console.log(`\n📊 Manager Assignment Summary:`);
    console.log(`   - Area-level assignments created: ${areaAssignmentsCreated} (from areaManager field)`);
    console.log(`   - Store-level assignments created: ${storeAssignmentsCreated} (from store.gm arrays)`);
    console.log(`   - Total assignments created: ${this.results.managers.created}`);
    console.log(`   - Total area managers: ${areaManagers.size}`);
    console.log(`   - Total store managers: ${storeManagerMap.size}`);
  }

  /**
   * Helper: Find primary manager for an area
   */
  findPrimaryManager(stores) {
    const managerCounts = new Map();
    
    for (const store of stores) {
      if (store.gm && Array.isArray(store.gm)) {
        for (const managerId of store.gm) {
          managerCounts.set(managerId, (managerCounts.get(managerId) || 0) + 1);
        }
      }
    }

    if (managerCounts.size === 0) return null;
    
    // Return manager with most stores
    return Array.from(managerCounts.entries())
      .sort((a, b) => b[1] - a[1])[0][0];
  }

  /**
   * Helper: Find primary manager name
   */
  findPrimaryManagerName(stores) {
    const primaryManagerId = this.findPrimaryManager(stores);
    if (!primaryManagerId) return null;
    
    // TODO: Integrate with user system to get actual names
    // For now, return the manager ID with formatting
    return primaryManagerId.replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(/[\s_-]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Validate migration completeness
   */
  async validateMigration() {
    console.log(`🔍 Validating migration completeness...`);
    
    // Use cached data from initial fetch to avoid re-querying
    const expectedAreas = this.results.areas.created + this.results.areas.updated;
    const expectedStores = this.results.stores.created + this.results.stores.updated;

    console.log(`📊 Migration Results:`);
    console.log(`   Areas: Created ${this.results.areas.created}, Updated ${this.results.areas.updated}`);
    console.log(`   Stores: Created ${this.results.stores.created}, Updated ${this.results.stores.updated}, Skipped ${this.results.stores.skipped}`);
    console.log(`   Managers: ${this.results.managers.created} created`);

    // Log any errors
    const totalErrors = this.results.areas.errors.length + 
                       this.results.stores.errors.length + 
                       this.results.managers.errors.length;

    if (totalErrors === 0) {
      console.log(`✅ Migration validation successful - no errors detected!`);
    } else {
      console.log(`⚠️  Migration completed with ${totalErrors} errors`);
      
      if (this.results.areas.errors.length > 0) {
        console.log(`\n❌ Area Errors: ${this.results.areas.errors.length}`);
        this.results.areas.errors.forEach(err => console.log(`   - ${err.areaName}: ${err.error}`));
      }

      if (this.results.stores.errors.length > 0) {
        console.log(`\n❌ Store Errors: ${this.results.stores.errors.length}`);
        this.results.stores.errors.forEach(err => console.log(`   - ${err.storeName} (${err.action}): ${err.error}`));
      }

      if (this.results.managers.errors.length > 0) {
        console.log(`\n❌ Manager Errors: ${this.results.managers.errors.length}`);
        this.results.managers.errors.forEach(err => console.log(`   - ${err.managerId}: ${err.error}`));
      }
    }

    return {
      isValid: totalErrors === 0,
      totalErrors,
      expectedCounts: { expectedAreas, expectedStores },
      actualCounts: {
        areasCreated: this.results.areas.created,
        areasUpdated: this.results.areas.updated,
        storesCreated: this.results.stores.created,
        storesUpdated: this.results.stores.updated,
        managers: this.results.managers.created
      }
    };
  }
}

// Usage function
export async function runMigrationV2(clientId = 'grm') {
  const migration = new HierarchyMigrationV2(clientId);
  return await migration.migrate();
}

// Export for testing
export { HierarchyMigrationV2 };

/**
 * Example usage:
 * 
 * import { runMigrationV2 } from './scripts/migrateHierarchyDataV2.js';
 * 
 * // Run migration
 * const results = await runMigrationV2('grm');
 * console.log('Migration completed:', results);
 */